import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '@narvar/nth-button';
import s from '../exchangeSidebar.module.css';
import ButtonMenu from '../../../react-components/ButtonMenu';
import ReturnsSelect from '../../../react-components/ReturnsSelect';
import SidebarSectionTitle from '../../../react-components/SidebarSectionTitle/SidebarSectionTitle';
import ExchangeComment from '../components/ExchangeComment';
import { handlePhotoUpload } from '../../../lib/reasons';
import PhotoUploader from '../../../react-components/PhotoUploader';
import { Spinner } from '../../Spinner';

export const mapStateToProps = ({
  claims: { enabled: claimsEnabled } = {},
  page: { productType, languageCode } = {},
  returnsSettings: {
    [productType]: {
      disableSidebarComments,
      exchange: { hide_sold_out_inventory: hideSoldOutInventory = false } = {},
    } = {},
  } = {},
  photoUpload: { isUploading, images } = {},
  language,
  language: {
    'returns.step_1.exchange_eligibility_only_header': exchangeOnlyMessage,
    'returns.step_1.exchange_item_out_of_stock': outOfStockErrorMessage,
    'returns.step_1.sidepanel_exchange_additional_comments': exchangeCommentTitle,
    'returns.step_1.sidepanel_exchange_comments_description': exchangeCommentDescription,
    'returns.step_1.sidepanel_next': submitBtnText,
    'returns.step_1.sidepanel_dont_exchange': cancelExchangeBtnText,
    'returns.step_1.sidepanel_exchange_reason_title': exchangeReasonTitle,
  } = {},
  itemsPage: { isFetchingAddCartItem, isFetchingRemoveCartItem } = {},
}) => ({
  hideSoldOutInventory,
  productChoiceTitle: claimsEnabled
    ? language['returns.step_1.item_sidepanel_product_choice']
    : language['returns.step_1.sidepanel_product_choice'],
  exchangeOnlyMessage,
  outOfStockErrorMessage,
  exchangeCommentTitle,
  exchangeCommentDescription,
  submitBtnText,
  cancelExchangeBtnText,
  exchangeReasonTitle,
  disableSidebarComments,
  isUploading,
  images,
  languageCode,
  isFetchingAddCartItem,
  isFetchingRemoveCartItem,
});

const ExchangeMenu = ({
  dispatch,
  hideSoldOutInventory,
  returnEligibility,
  productChoiceTitle,
  exchangeOnlyMessage,
  productSelectMenu,
  productSelectOnClick,
  checkSelected,
  itemSaved,
  outOfStockError,
  outOfStockErrorMessage,
  options,
  exchangeOptions,
  language,
  exchangeOptionSelectOnChange,
  exchangeOptionSelectOnFocus,
  exchangeSelectedOptions,
  itemQuantity,
  itemQtyOptions,
  exchangeSelectionRequired,
  quantitySelectOnChange,
  quantitySelectOnFocus,
  returnQuantity,
  isCommentBoxCountDisabled,
  exchangeCommentTitle,
  exchangeComment,
  maxCommentLength,
  commentPlaceholder,
  commentHidden,
  commentRequired,
  exchangeCommentDescription,
  exchangeCommentOnChange,
  submitOnClick,
  submitBtnText,
  cancelOnClick,
  cancelExchangeBtnText,
  exchangeReasons,
  nestedReasons,
  checkReasonIsSelected,
  selectedReason,
  selectedNestedReason,
  exchangeReasonTitle,
  nestedReasonsTitle,
  handleSelectReason,
  resetReasons,
  disableSidebarComments,
  isUploading,
  images,
  photoUploadEnabled,
  photoUploadRequired,
  photoUploadTitle,
  photoUploadPlaceholder,
  photoUploadMaxFiles,
  languageCode,
  isFetchingAddCartItem,
  isFetchingRemoveCartItem,
}) => {
  const isFetching = isUploading || isFetchingAddCartItem || isFetchingRemoveCartItem;
  const quantityOptions = Array(itemQuantity)
    .fill(0)
    .map((qty, index) => index + 1);

  const hasExchangeReasons = exchangeReasons?.length > 0;
  const parentReasonSelectionRequired = hasExchangeReasons && !selectedReason;
  const nestedReasonSelectionRequired = nestedReasons && !selectedNestedReason;
  const showCommentBox = (!commentHidden || commentRequired) && !disableSidebarComments;
  const commentIsRequired = commentRequired && !exchangeComment;
  const disableSubmit =
    exchangeSelectionRequired ||
    parentReasonSelectionRequired ||
    nestedReasonSelectionRequired ||
    commentIsRequired ||
    isUploading ||
    (photoUploadRequired && images.length < 1);

  const showExchangeOptions =
    options &&
    options.length > 0 &&
    !parentReasonSelectionRequired &&
    !nestedReasonSelectionRequired;

  return (
    <div>
      {returnEligibility && <SidebarSectionTitle index={0} title={productChoiceTitle} />}
      {returnEligibility ? (
        <ButtonMenu
          menuItems={[productSelectMenu.find(({ type }) => type === 'exchange')]}
          itemKey="type"
          propName="displayName"
          itemClick={productSelectOnClick}
          checkSelected={checkSelected}
          disabled={Boolean(itemSaved)}
        />
      ) : (
        <div> {exchangeOnlyMessage} </div>
      )}

      {outOfStockError && <div className={s.error_message}> {outOfStockErrorMessage} </div>}

      {!outOfStockError && hasExchangeReasons && (
        <React.Fragment>
          <SidebarSectionTitle index={0} title={exchangeReasonTitle} />
          <ButtonMenu
            menuItems={selectedReason ? [selectedReason] : exchangeReasons}
            itemKey="reason_category"
            propName="reason_category"
            itemClick={(index) => {
              if (isFetching) return;
              if (selectedReason) {
                resetReasons();
              } else {
                handleSelectReason(index);
              }
            }}
            checkSelected={checkReasonIsSelected}
          />
        </React.Fragment>
      )}

      {nestedReasons && (
        <div className="nested-reasons-container">
          <SidebarSectionTitle
            title={nestedReasonsTitle || language['returns.step_1.sidepanel_tell_us_more']}
          />
          <ButtonMenu
            menuItems={nestedReasons}
            itemKey="return_reason_code_id"
            propName="display_name"
            itemClick={(index) => {
              if (isFetching) return;
              handleSelectReason(index);
            }}
            checkSelected={checkReasonIsSelected}
          />
        </div>
      )}

      {showExchangeOptions && (
        <React.Fragment>
          {options.map((option) => (
            <ReturnsSelect
              key={option.code}
              exchangeCriteria={option.code}
              optionDisabledLabel="item_out_of_stock_label"
              exchangeOptions={exchangeOptions}
              language={language}
              onChange={exchangeOptionSelectOnChange(option.code)}
              onFocus={exchangeOptionSelectOnFocus(option.code, option.values)}
              selectedOptions={exchangeSelectedOptions}
              hideUnavailable={hideSoldOutInventory}
              clearable
            />
          ))}
          {itemQuantity > 1 && (
            <ReturnsSelect
              key={0}
              exchangeCriteria="quantity"
              disabled={exchangeSelectionRequired}
              optionDisabledLabel="qty_unavailable_label"
              exchangeOptions={{ quantity: itemQtyOptions, quantitySortOrder: quantityOptions }}
              language={language}
              onChange={quantitySelectOnChange}
              onFocus={quantitySelectOnFocus}
              selectedOptions={{
                quantity: exchangeSelectionRequired ? '' : returnQuantity,
              }}
              hideUnavailable={hideSoldOutInventory}
            />
          )}
          {!hasExchangeReasons && (
            <ExchangeComment
              title={exchangeCommentTitle}
              isCommentBoxCountDisabled={isCommentBoxCountDisabled}
              exchangeComment={exchangeComment}
              maxCommentLength={maxCommentLength}
              commentPlaceholder={commentPlaceholder || exchangeCommentDescription}
              onChange={exchangeCommentOnChange}
            />
          )}
          {photoUploadEnabled && (
            <PhotoUploader
              title={photoUploadTitle}
              placeholder={photoUploadPlaceholder}
              onDrop={(acceptedFiles, rejectedFiles) =>
                handlePhotoUpload({
                  acceptedFiles,
                  rejectedFiles,
                  images,
                  photoUploadMaxFiles,
                  dispatch,
                  language,
                  languageCode,
                })
              }
            />
          )}
          {hasExchangeReasons && showCommentBox && (
            <ExchangeComment
              title={
                commentRequired
                  ? language['returns.step_1.sidepanel_exchange_additional_comments_mandatory']
                  : language['returns.step_1.sidepanel_exchange_additional_comments']
              }
              isCommentBoxCountDisabled={isCommentBoxCountDisabled}
              exchangeComment={exchangeComment}
              maxCommentLength={maxCommentLength}
              commentPlaceholder={commentPlaceholder || exchangeCommentDescription}
              onChange={exchangeCommentOnChange}
            />
          )}
        </React.Fragment>
      )}

      <Button
        className="narvar-btn-primary sidebar-done-button sidebar-action-btn"
        disabled={
          isFetching || disableSubmit || !(itemQuantity > 1 ? Boolean(returnQuantity) : true)
        }
        big
        s={{ marginTop: '2rem' }}
        onClick={submitOnClick}
      >
        <span data-test="sidebar-done-button">{submitBtnText}</span>
        <div className={s.button_spinner_container}>
          <span className="returns-spinner">{isFetchingAddCartItem && <Spinner width="30" />}</span>
        </div>
      </Button>

      {itemSaved && !disableSubmit && (
        <Button
          className="narvar-btn-tertiary sidebar-cancel-item-button sidebar-action-btn"
          onClick={cancelOnClick}
          s={{ marginTop: '1rem' }}
          big
        >
          <span data-test="do-not-exchange-button">{cancelExchangeBtnText}</span>
          <div className={s.button_spinner_container}>
            <span className="returns-spinner">
              {isFetchingRemoveCartItem && <Spinner width="30" />}
            </span>
          </div>
        </Button>
      )}
    </div>
  );
};

ExchangeMenu.defaultProps = {
  hideSoldOutInventory: false,
  returnEligibility: false,
  productChoiceTitle: '',
  exchangeOnlyMessage: '',
  productSelectMenu: [],
  itemSaved: null,
  outOfStockError: false,
  options: [],
  exchangeOptions: {},
  itemQuantity: null,
  itemQtyOptions: {},
  exchangeSelectedOptions: {},
  exchangeSelectionRequired: false,
  returnQuantity: 0,
  isCommentBoxCountDisabled: false,
  exchangeCommentTitle: '',
  exchangeComment: '',
  maxCommentLength: '',
  commentPlaceholder: '',
  commentHidden: undefined,
  commentRequired: undefined,
  exchangeCommentDescription: '',
  outOfStockErrorMessage: '',
  submitBtnText: '',
  cancelExchangeBtnText: '',
  exchangeReasons: undefined,
  nestedReasons: undefined,
  selectedReason: undefined,
  selectedNestedReason: undefined,
  exchangeReasonTitle: '',
  nestedReasonsTitle: '',
  disableSidebarComments: undefined,
  photoUploadEnabled: false,
  photoUploadRequired: false,
  photoUploadPlaceholder: '',
  photoUploadTitle: '',
  photoUploadMaxFiles: undefined,
  isFetchingAddCartItem: false,
  isFetchingRemoveCartItem: false,
};

ExchangeMenu.propTypes = {
  dispatch: PropTypes.func.isRequired,
  hideSoldOutInventory: PropTypes.bool,
  returnEligibility: PropTypes.bool,
  productChoiceTitle: PropTypes.string,
  productSelectMenu: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      displayName: PropTypes.string,
    }),
  ),
  productSelectOnClick: PropTypes.func.isRequired,
  checkSelected: PropTypes.func.isRequired,
  itemSaved: PropTypes.object,
  outOfStockError: PropTypes.bool,
  options: PropTypes.array,
  exchangeOptions: PropTypes.object,
  language: PropTypes.object.isRequired,
  exchangeOptionSelectOnChange: PropTypes.func.isRequired,
  exchangeOptionSelectOnFocus: PropTypes.func.isRequired,
  exchangeSelectedOptions: PropTypes.object,
  itemQuantity: PropTypes.number,
  itemQtyOptions: PropTypes.object,
  exchangeSelectionRequired: PropTypes.bool,
  quantitySelectOnChange: PropTypes.func.isRequired,
  quantitySelectOnFocus: PropTypes.func.isRequired,
  returnQuantity: PropTypes.number,
  isCommentBoxCountDisabled: PropTypes.bool,
  exchangeCommentTitle: PropTypes.string,
  exchangeComment: PropTypes.string,
  maxCommentLength: PropTypes.number,
  commentPlaceholder: PropTypes.string,
  commentHidden: PropTypes.bool,
  commentRequired: PropTypes.bool,
  exchangeCommentDescription: PropTypes.string,
  exchangeCommentOnChange: PropTypes.func.isRequired,
  submitOnClick: PropTypes.func.isRequired,
  cancelOnClick: PropTypes.func.isRequired,
  exchangeOnlyMessage: PropTypes.string,
  outOfStockErrorMessage: PropTypes.string,
  submitBtnText: PropTypes.string,
  cancelExchangeBtnText: PropTypes.string,
  exchangeReasons: PropTypes.arrayOf(PropTypes.object),
  nestedReasons: PropTypes.arrayOf(PropTypes.object),
  checkReasonIsSelected: PropTypes.func.isRequired,
  selectedReason: PropTypes.object,
  selectedNestedReason: PropTypes.object,
  exchangeReasonTitle: PropTypes.string,
  nestedReasonsTitle: PropTypes.string,
  handleSelectReason: PropTypes.func.isRequired,
  resetReasons: PropTypes.func.isRequired,
  disableSidebarComments: PropTypes.bool,
  isUploading: PropTypes.bool.isRequired,
  images: PropTypes.array.isRequired,
  photoUploadEnabled: PropTypes.bool,
  photoUploadRequired: PropTypes.bool,
  photoUploadPlaceholder: PropTypes.string,
  photoUploadTitle: PropTypes.string,
  photoUploadMaxFiles: PropTypes.number,
  languageCode: PropTypes.string.isRequired,
  isFetchingAddCartItem: PropTypes.bool,
  isFetchingRemoveCartItem: PropTypes.bool,
};

export default connect(mapStateToProps)(ExchangeMenu);
